import axios from 'axios'
import Cookies from 'js-cookie'

import { Routes } from '~/api/api-routes'
import { fetchUserConfig, updateUserConfig } from '~/api/common'
import { claimReward, fetchOnboardingStatus } from '~/api/common/onboarding-status'

import * as types from '../mutation-types'
import { broadcastLogout } from '~/helpers/authWatcher'
import { ubbUserHelper } from '~/helpers/ubbUserHelper'
import { clearOnboardingLocalStorage } from '~/store/modules/onboarding-store-wizard'

// state
export const state = {
  user: null,
  token: Cookies.get('token'),
  activeStore: null,
  userConfig: {},
  onboardingStatus: null,
}

// getters
export const getters = {
  user: (state) => state.user,
  token: (state) => state.token,
  check: (state) => state.user !== null,
  hasTiktokStore: (state) => {
    const type = state.activeStore?.store_type
    return type === 'tiktok' || type === 'tiktok_global'
  },
  hasTiktokWarehouse: (state) => state.activeStore?.location_id !== null,
  activeStore: (state) => state.activeStore,
  userConfig: (state) => state.userConfig,
  isPaUser: (state) => state.user?.role_id === 4 || false,
  isFreeUser: (state) => state.user?.is_free_user,
  isGlobalTiktokStore: (state) => state.activeStore?.store_type === 'tiktok_global',
  onboardingStatus: (state) => state.onboardingStatus,
}

// mutations
export const mutations = {
  [types.SAVE_TOKEN](state, { token }) {
    state.token = token
  },

  [types.FETCH_USER_SUCCESS](state, { user }) {
    state.user = user
  },

  [types.FETCH_USER_FAILURE](state) {
    state.token = null
  },

  [types.LOGOUT](state) {
    state.user = null
    state.token = null
    clearOnboardingLocalStorage()
    try {
      localStorage.removeItem('zd:ai-chat-onboarding')
    } catch {
      // ignore storage errors
    }
    Cookies.remove('token', { domain: config.cookie_domain, path: '/' })
  },

  [types.UPDATE_USER](state, { user }) {
    state.user = user
  },

  setActiveStore(state, store) {
    state.activeStore = store
  },

  setUserConfig(state, value) {
    state.userConfig = value
  },

  updateUserConfig(state, { key, value }) {
    state.userConfig[key] = value
  },

  setOnboardingStatus(state, value) {
    state.onboardingStatus = value
  },

  setSKUVisualPositive(state, value) {
    if (state.user && state.user.user_settings) {
      state.user = {
        ...state.user,
        user_settings: {
          ...state.user.user_settings,
          show_sku: value,
        },
      }
    }
  },

  setSeenFirstOrderModal(state, value) {
    if (state.user && state.user.user_settings) {
      state.user = {
        ...state.user,
        user_settings: {
          ...state.user.user_settings,
          seen_first_order_modal: value,
        },
      }
    }
  },
}

// actions
export const actions = {
  saveToken({ commit, dispatch }, payload) {
    commit(types.SAVE_TOKEN, payload)
  },

  async fetchActiveStore({ state, commit }) {
    if (!state.activeStore) return
    const apiRoute = Routes.myStores.store(state.activeStore.id)
    const { data } = await axios.get(`${window.config.userurl}/${apiRoute}`)
    commit('setActiveStore', data)
  },

  async fetchUserConfig({ commit }, keys = []) {
    const query = keys.join(',')
    const data = await fetchUserConfig(query)
    const parsedConfig = data.reduce((acc, item) => {
      acc[item.key] = item.context.value
      return acc
    }, {})
    commit('setUserConfig', parsedConfig)
  },

  async updateUserConfig({ commit }, data) {
    const response = await updateUserConfig(data)
    const parsedConfig = response.reduce((acc, item) => {
      acc[item.key] = item.context.value
      return acc
    }, {})
    commit('updateUserConfig', { key: data.key, value: parsedConfig[data.key] })
  },

  async fetchUser({ commit, dispatch }) {
    try {
      const apiRoute = Routes.userProfile.user
      const storeId = localStorage.getItem('activeStore')
      const { data: user } = await axios.get(
        `${window.config.userurl}/${apiRoute}`,
        storeId ? { params: { storeId } } : undefined,
      )

      const permissions = ubbUserHelper.getUserPermissionsForTier(user?.usage_based_billing?.current_tier?.name, user)
      if (permissions?.length) user.user_permissions = permissions

      commit(types.FETCH_USER_SUCCESS, { user })
      dispatch('identifyImpactUser')
    } catch (e) {
      commit(types.FETCH_USER_FAILURE)
    }
  },

  updateUser({ commit }, payload) {
    commit(types.UPDATE_USER, payload)
  },

  async logout({ commit }) {
    try {
      await axios.post(Routes.userProfile.logout)
    } catch (e) {}

    commit(types.LOGOUT)
    broadcastLogout()
  },

  async fetchOauthUrl(ctx, { provider }) {
    const url = Routes.userProfile.fetchOauthUrl(provider)
    const { data } = await axios.post(url)
    return data.url
  },

  async identifyImpactUser({ state }) {
    if (typeof ire === 'function') {
      ire('identify', {
        customerId: state.user.id ?? '',
        customerEmail: state.user.email ? state.user.email_sha1_attribute : '',
      })
    } else {
      console.log('ire function not found')
    }
  },

  async fetchOnboardingStatus({ commit, state }) {
    if (!state.activeStore?.id) return
    const data = await fetchOnboardingStatus(state.activeStore.id)
    commit('setOnboardingStatus', data)
  },

  async claimReward({ dispatch, state }) {
    if (!state.activeStore?.id) return
    await claimReward(state.activeStore.id)
    await dispatch('fetchOnboardingStatus')
  },

  setSKUVisualPositive({ commit }, value) {
    commit('setSKUVisualPositive', value)
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
